/**
 * Материалы строки техкарты — «норма расхода × плановая цена» (макет v2-05).
 *
 * Позиции берутся из БЮДЖЕТНОГО справочника сезона (ADR-066 D4): цену для
 * расчёта ₽/га можно взять только оттуда, поэтому в списке — позиции бюджета,
 * а не тенантная номенклатура. Позиция без плановой цены заводить можно, но
 * экран честно предупреждает, что она в себестоимость не попадёт.
 *
 * Набор сохраняется ЦЕЛИКОМ (PUT): «добавил один, убрал другой» обязано
 * доехать одной транзакцией, иначе на экране одно, а в базе другое.
 *
 * Сев (требование founder, UAT 20.08): у СЕМЕННОЙ позиции в операции сева три
 * поля показываются СРАЗУ, а не прячутся за «добавить» — сорт/гибрид, норма
 * высева с единицей (кг/га либо п.е./га) и густота посева (шт / тыс. шт /
 * млн шт на гектар). У несемянных материалов сева (удобрение при севе) всё
 * как было: только норма.
 */
import { formatPrice } from '@/lib/planning/money';
import { useMemo, useState } from 'react';
import { Check, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { matchesSearch } from '@/lib/reference/normalizeSearch';
import {
  SECTION_TITLES,
  TMC_SECTIONS,
  useBudgetSection,
  type BudgetItem,
  type BudgetSection,
} from '@/lib/planning/budgetCatalog';
import {
  formatNumber,
  useCropSeedMass,
  type MaterialInput,
  type TechCardOperation,
} from '@/lib/planning/techCards';
import { useCropVarieties } from '@/lib/planning/planCrops';
import { useMeasurementUnits, type MeasurementUnit } from '@/lib/planning/budgetCatalog';
import {
  DENSITY_UNIT_CODES,
  SEED_RATE_UNIT_CODES,
  isSeedMaterial,
  parseOptionalNumber,
  seedRateFromDensity,
  unitOptions,
} from '@/lib/planning/seedMaterials';
import { cn } from '@/lib/utils';

const BASIS_TITLES: Record<MaterialInput['rate_basis'], string> = {
  per_ha: 'на гектар',
  per_ton: 'на тонну',
  per_unit: 'на операцию',
};

interface Draft extends MaterialInput {
  key: string;
  name: string;
  price: number | null;
  /** Раздел бюджета позиции — определяет, семенная она или нет. */
  section: string | null;
  /** Имя сорта из ответа сервера — показываем, пока грузится справочник. */
  varietyName: string | null;
  /**
   * Густота живёт в черновике СТРОКОЙ, а не числом: «пусто ≠ мусор». Пустое
   * поле — законное «не заполнено», «абв» — ошибка, о которой форма скажет
   * словами при сохранении, а не молча оставит прежнее число.
   */
  densityRaw: string;
}

function parseRate(raw: string): number | undefined {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.');
  const value = Number(trimmed);
  if (!trimmed || !Number.isFinite(value) || value < 0) return undefined;
  return value;
}

export function MaterialsDialog({
  operation,
  seasonId,
  cropId,
  saving,
  onSubmit,
  onClose,
}: {
  operation: TechCardOperation;
  seasonId: string;
  /** Культура карты — по ней фильтруются сорта в выпадашке сева. */
  cropId: string | null;
  saving: boolean;
  onSubmit: (materials: MaterialInput[]) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<Draft[]>(() =>
    (operation.materials ?? []).map((m, i) => ({
      key: m.id ?? `row-${i}`,
      budget_item_id: m.budget_item_id,
      rate: m.rate,
      rate_basis: m.rate_basis,
      rate_unit_id: m.rate_unit_id,
      area_share_pct: m.area_share_pct ?? 100,
      variety_id: m.variety_id ?? null,
      varietyName: m.variety_name ?? null,
      seeding_density_unit_id: m.seeding_density_unit_id ?? null,
      densityRaw: m.seeding_density === null || m.seeding_density === undefined ? '' : String(m.seeding_density),
      section: m.budget_item_section ?? null,
      notes: m.notes,
      name: m.budget_item_name ?? 'позиция без названия',
      price: m.planned_price,
    })),
  );
  const [error, setError] = useState<string | null>(null);
  const units = useMeasurementUnits();
  const seedMass = useCropSeedMass(cropId);

  function patchRow(key: string, patch: Partial<Draft>) {
    setRows((cur) => cur.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  function addItem(item: BudgetItem) {
    setRows((cur) => {
      if (cur.some((r) => r.budget_item_id === item.id)) return cur;
      return [
        ...cur,
        {
          key: `new-${item.id}`,
          budget_item_id: item.id,
          rate: 1,
          rate_basis: 'per_ha',
          rate_unit_id: item.unit_id,
          area_share_pct: 100,
          // Семенная позиция, сопоставленная с сортом справочника, приносит
          // сорт с собой — это не догадка, а то самое сопоставление.
          variety_id: item.section === 'seed' ? item.ref_id : null,
          varietyName: null,
          seeding_density_unit_id: null,
          densityRaw: '',
          section: item.section,
          notes: null,
          name: item.name,
          price: item.planned_price,
        },
      ];
    });
  }

  function submit() {
    if (rows.some((r) => !Number.isFinite(r.rate) || r.rate < 0)) {
      setError('Норма расхода — неотрицательное число.');
      return;
    }
    if (rows.some((r) => (r.area_share_pct ?? 100) <= 0 || (r.area_share_pct ?? 100) > 100)) {
      setError('Доля площади материала — процент от 0 (не включая) до 100.');
      return;
    }
    const densities = new Map<string, number | null>();
    for (const row of rows) {
      const parsed = parseOptionalNumber(row.densityRaw);
      if (parsed === 'invalid') {
        setError(`Густота посева «${row.name}» — неотрицательное число, например 65 или 0,9.`);
        return;
      }
      if (parsed !== null && !row.seeding_density_unit_id) {
        setError('Укажите, в чём густота посева — в штуках, тысячах или миллионах штук на гектар.');
        return;
      }
      if (parsed === null && row.seeding_density_unit_id) {
        setError('Единица густоты выбрана, а само число не введено — впишите густоту посева.');
        return;
      }
      densities.set(row.key, parsed);
    }
    setError(null);
    onSubmit(
      rows.map(({ key, budget_item_id, rate, rate_basis, rate_unit_id, area_share_pct, variety_id, seeding_density_unit_id, notes }, seq) => ({
        budget_item_id,
        rate,
        rate_basis,
        rate_unit_id,
        area_share_pct: area_share_pct ?? 100,
        variety_id: variety_id ?? null,
        seeding_density: densities.get(key) ?? null,
        seeding_density_unit_id: seeding_density_unit_id ?? null,
        notes,
        seq,
      })),
    );
  }

  const noPrice = rows.filter((r) => r.price === null);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Материалы операции «{operation.name ?? operation.op_type_name ?? 'без названия'}»
          </DialogTitle>
          <DialogDescription>
            Норма расхода × плановая цена из бюджетного справочника сезона. Цена правится
            там же, в справочнике — здесь только норма.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground">
              У этой операции материалов нет. Для механической обработки это нормально; для
              сева и обработок — добавьте семена, удобрения или препарат.
            </p>
          )}

          <ul className="space-y-2">
            {rows.map((row) => {
              const seed = isSeedMaterial(operation.block_kind, row.section);
              return (
              <li
                key={row.key}
                className="min-h-10 rounded-md border border-border p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-foreground">{row.name}</span>
                <span className="text-xs text-muted-foreground">
                  {row.price === null
                    ? 'плановой цены нет — в себестоимость не попадёт'
                    : formatPrice(row.price)}
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <Label htmlFor={`rate-${row.key}`} className="text-xs text-muted-foreground">
                    норма
                  </Label>
                  <Input
                    id={`rate-${row.key}`}
                    className="h-8 w-24"
                    inputMode="decimal"
                    defaultValue={String(row.rate)}
                    onChange={(e) => {
                      const parsed = parseRate(e.target.value);
                      if (parsed === undefined) return;
                      setRows((cur) =>
                        cur.map((r) => (r.key === row.key ? { ...r, rate: parsed } : r)),
                      );
                    }}
                  />
                  <Label htmlFor={`share-${row.key}`} className="text-xs text-muted-foreground">
                    % площади
                  </Label>
                  <Input
                    id={`share-${row.key}`}
                    className="h-8 w-20"
                    inputMode="decimal"
                    title="Доля площади ЭТОГО материала — своя: элита на 7 % клина, обычные семена на 93 %"
                    defaultValue={String(row.area_share_pct ?? 100)}
                    onChange={(e) => {
                      const parsed = parseRate(e.target.value);
                      if (parsed === undefined) return;
                      setRows((cur) =>
                        cur.map((r) => (r.key === row.key ? { ...r, area_share_pct: parsed } : r)),
                      );
                    }}
                  />
                  {seed ? (
                    // У семян норму задают В ЕДИНИЦЕ (кг/га либо п.е./га), а не
                    // «в чём-то на гектар» — селектор базы для них лишний.
                    // Строку с нестандартной базой (осталась из старых данных)
                    // база всё равно покажет: молча переписать её нельзя.
                    <UnitSelect
                      label={`Единица нормы высева — ${row.name}`}
                      value={row.rate_unit_id ?? null}
                      options={unitOptions(units.data, SEED_RATE_UNIT_CODES, row.rate_unit_id)}
                      emptyLabel="единица не выбрана"
                      onChange={(id) => patchRow(row.key, { rate_unit_id: id })}
                    />
                  ) : null}
                  {seed && row.rate_basis === 'per_ha' ? null : (
                  <select
                    aria-label={`На что считаем норму — ${row.name}`}
                    value={row.rate_basis}
                    onChange={(e) =>
                      setRows((cur) =>
                        cur.map((r) =>
                          r.key === row.key
                            ? { ...r, rate_basis: e.target.value as MaterialInput['rate_basis'] }
                            : r,
                        ),
                      )
                    }
                    className="h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground"
                  >
                    {(Object.keys(BASIS_TITLES) as MaterialInput['rate_basis'][]).map((basis) => (
                      <option key={basis} value={basis}>
                        {BASIS_TITLES[basis]}
                      </option>
                    ))}
                  </select>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Убрать материал ${row.name}`}
                    onClick={() => setRows((cur) => cur.filter((r) => r.key !== row.key))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                </div>
                {seed && (
                  <SeedFields
                    row={row}
                    cropId={cropId}
                    units={units.data}
                    seedMass={seedMass}
                    onPatch={(patch) => patchRow(row.key, patch)}
                  />
                )}
              </li>
              );
            })}
          </ul>

          <BudgetItemPicker seasonId={seasonId} onPick={addItem} />

          {noPrice.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Без плановой цены: {noPrice.map((r) => r.name).join(', ')}. Заполните цену в
              бюджетных справочниках сезона — ₽/га пересчитается само.
            </p>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={saving}>
            Сохранить материалы
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Селектор единицы. Пустое значение — законное «не выбрана», а не подставленная
 * за агронома единица: подставить кг там, где имелись в виду посевные единицы,
 * значит соврать в себестоимости.
 */
function UnitSelect({
  label,
  value,
  options,
  emptyLabel,
  onChange,
}: {
  label: string;
  value: string | null;
  options: { id: string; label: string }[];
  emptyLabel: string;
  onChange: (id: string | null) => void;
}) {
  return (
    <select
      aria-label={label}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      className="h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground"
    >
      <option value="">{emptyLabel}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Поля семенной позиции в севе: сорт/гибрид и густота посева.
 *
 * Норма высева живёт в общей строке (это та же норма, что у любого материала,
 * просто с другой единицей) — дублировать её здесь нельзя: два поля одного
 * смысла разъезжаются на первом же расчёте.
 */
function SeedFields({
  row,
  cropId,
  units,
  seedMass,
  onPatch,
}: {
  row: Draft;
  cropId: string | null;
  units: MeasurementUnit[] | undefined;
  seedMass: { minG: number | null; maxG: number | null };
  onPatch: (patch: Partial<Draft>) => void;
}) {
  const densityOptions = unitOptions(units, DENSITY_UNIT_CODES, row.seeding_density_unit_id);
  const densityCode = densityOptions.find((o) => o.id === row.seeding_density_unit_id)?.code ?? null;
  const parsedDensity = parseOptionalNumber(row.densityRaw);
  const derived =
    parsedDensity === 'invalid'
      ? null
      : seedRateFromDensity(parsedDensity, densityCode, seedMass.minG, seedMass.maxG);

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-border pt-2">
      <Label className="text-xs text-muted-foreground">сорт или гибрид</Label>
      <VarietyPicker
        cropId={cropId}
        valueId={row.variety_id ?? null}
        fallbackName={row.varietyName}
        onPick={(id) => onPatch({ variety_id: id })}
      />
      <Label htmlFor={`density-${row.key}`} className="text-xs text-muted-foreground">
        густота посева
      </Label>
      <Input
        id={`density-${row.key}`}
        className="h-8 w-24"
        inputMode="decimal"
        placeholder="напр. 65"
        value={row.densityRaw}
        onChange={(e) => onPatch({ densityRaw: e.target.value })}
      />
      <UnitSelect
        label={`Единица густоты посева — ${row.name}`}
        value={row.seeding_density_unit_id ?? null}
        options={densityOptions}
        emptyLabel="единица не выбрана"
        onChange={(id) => onPatch({ seeding_density_unit_id: id })}
      />
      {derived && (
        // Подсказка, а не автозаполнение: масса 1000 семян в справочнике —
        // диапазон, и норму агроном ставит свою.
        <span className="text-xs text-muted-foreground">
          при массе 1000 семян из справочника это примерно{' '}
          {derived.minKgHa === derived.maxKgHa
            ? `${formatNumber(derived.minKgHa)} кг/га`
            : `${formatNumber(derived.minKgHa)}–${formatNumber(derived.maxKgHa)} кг/га`}
        </span>
      )}
    </div>
  );
}

/** Сорт культуры карты: алфавит + поиск, без «топ-N» (сквозной паттерн). */
function VarietyPicker({
  cropId,
  valueId,
  fallbackName,
  onPick,
}: {
  cropId: string | null;
  valueId: string | null;
  fallbackName: string | null;
  onPick: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const list = useCropVarieties(cropId);
  const options = useMemo(
    () => (list.data ?? []).filter((v) => matchesSearch(v.name, term)),
    [list.data, term],
  );
  const selected = (list.data ?? []).find((v) => v.id === valueId);
  const title = selected?.name ?? fallbackName ?? 'сорт не выбран';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-8 max-w-56 justify-start truncate">
          {title}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="relative border-b border-border p-2">
          <Search className="absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Поиск сорта…"
            className="h-8 pl-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {!cropId && <p className="p-2 text-sm text-muted-foreground">Культура карты не определена.</p>}
          {cropId && list.isLoading && <p className="p-2 text-sm text-muted-foreground">Загружаем…</p>}
          {cropId && !list.isLoading && options.length === 0 && (
            <p className="p-2 text-sm text-muted-foreground">
              Сортов этой культуры в справочнике нет — заведите сорт в справочнике сортов.
            </p>
          )}
          {valueId && (
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm text-muted-foreground hover:bg-accent"
              onClick={() => {
                onPick(null);
                setOpen(false);
              }}
            >
              Убрать сорт
            </button>
          )}
          {options.map((v) => (
            <button
              key={v.id}
              type="button"
              className={cn('flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent')}
              onClick={() => {
                onPick(v.id);
                setOpen(false);
              }}
            >
              <Check className={cn('h-4 w-4', v.id === valueId ? 'opacity-100' : 'opacity-0')} />
              <span className="text-foreground">{v.name}</span>
              {v.is_hybrid && <span className="ml-auto text-xs text-muted-foreground">гибрид</span>}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Позиция бюджета: раздел + алфавит + поиск, без «топ-N». */
function BudgetItemPicker({
  seasonId,
  onPick,
}: {
  seasonId: string;
  onPick: (item: BudgetItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState<BudgetSection>('pesticide');
  const [term, setTerm] = useState('');
  const list = useBudgetSection({ seasonId, section, farmId: null, includeArchived: false });

  const options = useMemo(() => {
    const all = [...(list.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
    return all.filter((item) => matchesSearch(item.name, term));
  }, [list.data, term]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          Добавить материал
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <div className="flex flex-wrap gap-1 border-b border-border p-2">
          {TMC_SECTIONS.map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={s === section}
              onClick={() => setSection(s)}
              className={
                s === section
                  ? 'rounded-full border border-primary bg-primary/10 px-2 py-1 text-xs font-medium text-primary transition-colors'
                  : 'rounded-full border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
              }
            >
              {SECTION_TITLES[s]}
            </button>
          ))}
        </div>
        <div className="relative border-b border-border p-2">
          <Search className="absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
          <Input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Поиск позиции…"
            className="h-8 pl-8"
          />
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {list.isLoading && <p className="p-2 text-sm text-muted-foreground">Загружаем…</p>}
          {!list.isLoading && options.length === 0 && (
            <p className="p-2 text-sm text-muted-foreground">
              В этом разделе бюджета сезона позиций нет — наполните справочник.
            </p>
          )}
          {options.map((item) => (
            <button
              key={item.id}
              type="button"
              className={cn(
                'flex w-full flex-wrap items-center gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-accent',
              )}
              onClick={() => {
                onPick(item);
                setOpen(false);
              }}
            >
              <Check className="h-4 w-4 opacity-0" />
              <span className="text-foreground">{item.name}</span>
              <span className="ml-auto text-xs text-muted-foreground">
                {item.planned_price === null ? 'без цены' : formatPrice(item.planned_price)}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
